import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Signup() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/verify-email`,
      },
    });
    setLoading(false);
    if (error) {
      const msg = error.message || "";
      if (/rate.?limit|over_email|too many/i.test(msg)) {
        toast.error("Too many signup attempts. Please wait a minute and try again.");
      } else {
        toast.error(msg || "Signup failed");
      }
      return;
    }
    if (data.user && !data.session) {
      toast.success("Check your inbox to verify your email");
    } else {
      toast.success("Account created");
    }
    navigate("/verify-email");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[35%_65%] bg-[#FAF8F5]">
      <div className="flex flex-col justify-center px-8 md:px-14 py-12">
        <Link to="/" className="mb-10 inline-block">
          <Logo variant="full" size={40} />
        </Link>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1C1917] mb-2">Create your account</h1>
        <p className="text-[#57534E] mb-8">14-day free trial. No credit card required.</p>

        <form onSubmit={submit} className="space-y-5 max-w-md">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="signup-name" required value={fullName}
                   onChange={(e) => setFullName(e.target.value)}
                   className="mt-1.5 bg-white border-[#E8E2D9]" placeholder="Jane Doe" />
          </div>
          <div>
            <Label htmlFor="email">Work email</Label>
            <Input id="email" data-testid="signup-email" type="email" required autoComplete="email"
                   value={email} onChange={(e) => setEmail(e.target.value)}
                   className="mt-1.5 bg-white border-[#E8E2D9]" placeholder="you@company.com" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="signup-password" type="password" required minLength={8}
                   autoComplete="new-password"
                   value={password} onChange={(e) => setPassword(e.target.value)}
                   className="mt-1.5 bg-white border-[#E8E2D9]" placeholder="At least 8 characters" />
          </div>
          <Button data-testid="signup-submit" type="submit" disabled={loading}
                  className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F] py-6">
            {loading ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="text-sm text-[#57534E] mt-6">
          Already have an account? <Link to="/login" data-testid="link-login" className="text-[#E8712C] font-medium">Sign in</Link>
        </p>
        <p className="text-xs text-[#A8A29E] mt-8 max-w-md leading-relaxed">
          By creating an account you agree to our <Link to="/terms" className="text-[#E8712C] underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#E8712C] underline">Privacy Policy</Link>. You can delete your account at any time from Settings → Account. See <Link to="/data-deletion" className="text-[#E8712C] underline">Data Deletion</Link>.
        </p>
      </div>
      <div className="hidden lg:block bg-gradient-to-br from-[#2C3A29] to-[#3E4C38]" />
    </div>
  );
}
